import { SLOT, type Question, type Sym } from "./questions/question";

/*
  Hints draw the question rather than answer it: a number line for adding and
  taking away, an array of dots for times and sharing. The child still does
  the counting. Tier 5 questions (two-step, 2-digit × 1-digit) have no hint.
  Pure.
*/

/** How many dots an array may show before it stops being a help. */
export const MAX_DOTS = 144;

export type Hint =
  | {
      kind: "line";
      from: number;
      to: number;
      /** Numbers to mark on the line. */
      marks: number[];
      /** A jump drawn from a mark, `by` places along (negative goes back), landing unmarked. */
      jump: { from: number; by: number } | null;
    }
  | { kind: "array"; rows: number; cols: number };

/** a sym b = c, with exactly one of them the slot. */
function parts(
  q: Question,
): { a: number | null; sym: Sym; b: number | null; c: number | null } | null {
  const t = q.tokens;
  if (t.length !== 5 || t[3] !== "=") return null;
  const sym = t[1];
  if (typeof sym !== "string" || sym === "=" || sym === SLOT) return null;
  const num = (x: (typeof t)[number]) => (x === SLOT ? null : typeof x === "number" ? x : NaN);
  const a = num(t[0]!);
  const b = num(t[2]!);
  const c = num(t[4]!);
  if ([a, b, c].some((v) => v !== null && Number.isNaN(v))) return null;
  if ([a, b, c].filter((v) => v === null).length !== 1) return null;
  return { a, sym, b, c };
}

const down10 = (n: number) => Math.floor(n / 10) * 10;
const up10 = (n: number) => Math.max(10, Math.ceil(n / 10) * 10);

type Jump = { from: number; by: number } | null;

function line(values: number[], marks: number[], jump: Jump): Hint {
  const shown = [...marks, ...(jump ? [jump.from + jump.by] : [])];
  const lo = Math.min(...shown);
  const hi = Math.max(...values);
  // Short sums sit on a line from 0; longer ones on the tens around what's shown.
  const from = hi <= 20 ? 0 : down10(lo);
  const to = hi <= 20 ? (hi <= 10 ? 10 : 20) : up10(hi === down10(hi) ? hi + 1 : hi);
  return { kind: "line", from, to, marks, jump };
}

/** The hint for a question, or null when there isn't a useful one. */
export function hintFor(q: Question): Hint | null {
  if (q.tier >= 5) return null;
  const p = parts(q);
  if (!p) return null;
  const { a, sym, b, c } = p;
  const answer = q.answer;

  if (sym === "+" || sym === "−") {
    const back = sym === "−";
    // Every number in play, including the answer, sizes the line; the answer
    // itself is never marked.
    const all = [a, b, c, answer].filter((v): v is number => v !== null);
    if (c === null) return line(all, [a!], { from: a!, by: back ? -b! : b! }); // a ± b = ?
    if (b === null) return line(all, [a!, c], null); // a ± ? = c
    return line(all, [c], { from: c, by: back ? b : -b }); // ? ± b = c
  }

  // Times and sharing: rows of dots. The child counts rows, columns or all.
  let rows: number;
  let cols: number;
  if (sym === "×") {
    rows = a ?? answer;
    cols = b ?? answer;
  } else {
    // c ÷ b = a: c dots in rows of b make a rows.
    const dividend = a ?? answer;
    const divisor = b ?? answer;
    if (divisor === 0) return null;
    rows = dividend / divisor;
    cols = divisor;
  }
  if (!Number.isInteger(rows) || !Number.isInteger(cols)) return null;
  if (rows <= 0 || cols <= 0 || rows * cols > MAX_DOTS) return null;
  return { kind: "array", rows, cols };
}
